import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function AuthDialog({ open, onOpenChange, onSuccess }) {
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const [tab, setTab] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (tab === "login") {
        await login(email, password);
        toast.success(t("auth.success"));
      } else {
        await register(name, email, password);
        toast.success(t("auth.registered"));
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-border rounded-none sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {tab === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full rounded-none bg-[#F3EFE9]">
            <TabsTrigger value="login" data-testid="auth-tab-login" className="rounded-none text-xs tracking-[0.15em]">
              {t("auth.loginTab").toUpperCase()}
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register" className="rounded-none text-xs tracking-[0.15em]">
              {t("auth.registerTab").toUpperCase()}
            </TabsTrigger>
          </TabsList>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <TabsContent value="register" className="mt-0 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-name" className="text-xs tracking-[0.15em] text-muted-foreground">
                  {t("auth.name").toUpperCase()}
                </Label>
                <Input
                  id="auth-name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={tab === "register"}
                  className="rounded-none bg-[#F3EFE9] border-border focus-visible:ring-gold"
                />
              </div>
            </TabsContent>
            <div className="space-y-2">
              <Label htmlFor="auth-email" className="text-xs tracking-[0.15em] text-muted-foreground">
                {t("auth.email").toUpperCase()}
              </Label>
              <Input
                id="auth-email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none bg-[#F3EFE9] border-border focus-visible:ring-gold"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="auth-password" className="text-xs tracking-[0.15em] text-muted-foreground">
                {t("auth.password").toUpperCase()}
              </Label>
              <Input
                id="auth-password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-none bg-[#F3EFE9] border-border focus-visible:ring-gold"
              />
            </div>
            {error && (
              <p className="text-xs text-[#FF3333] tracking-wide" data-testid="auth-error-message">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full rounded-none bg-[#7D8C77] text-[#FAF8F5] hover:bg-[#3C3735] text-xs tracking-[0.2em] h-11"
            >
              {busy ? t("common.loading").toUpperCase() : (tab === "login" ? t("auth.loginBtn") : t("auth.registerBtn")).toUpperCase()}
            </Button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
